// RoleProvider — owns the active-role resolution pipeline (42.4 → 42.1 →
// 42.2) and the reactive mirror of `sessionStorage.currentRole` so pages
// can re-fetch scoped data on switch without a full page reload (42.3).
//
// Why a separate provider (vs folding into SessionProvider):
//   - SessionProvider is about fetching /api/session/me and handling 401s.
//   - RoleProvider is about picking / switching the active role. Keeping
//     them independent lets pages subscribe to role changes without
//     re-rendering on every unrelated session refresh, and vice-versa.
//
// Resolution order (DESIGN.md §9, task 42):
//   1. 42.4: `?as=<code>` URL param — if present AND, after short-code
//      translation, it matches a role in `distinct_roles`, use it.
//   2. 42.1: if `distinct_roles.length === 1`, auto-select it.
//   3. 42.2: otherwise set status=`pending` so the picker modal mounts and
//      admin nav is blocked.
//
// If `sessionStorage.currentRole` was already set (e.g. from a prior page
// load in this tab) AND the value is still a valid member of
// `distinct_roles`, we honor it — switching tabs back to an admin page
// should not re-prompt the picker.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import {
  clearCurrentRole,
  getCurrentRole,
  setCurrentRole as persistCurrentRole,
  setMissingRoleHandler,
} from "@/api";
import { useSession } from "@/session";
import { RoleContext, type RoleContextValue, type RoleStatus } from "./context";
import { readAsParam, resolveInitialRole } from "./resolver";

export function RoleProvider({ children }: { children: ReactNode }): JSX.Element {
  const { status: sessionStatus, session } = useSession();
  const location = useLocation();

  const [currentRole, setCurrentRoleState] = useState<string | null>(null);
  const [status, setStatus] = useState<RoleStatus>("resolving");
  const [scopedFetchToken, setScopedFetchToken] = useState<number>(0);

  // Distinct roles from session, stable-referenced so effects don't re-run
  // on every SessionProvider re-render.
  const distinctRoles = useMemo<readonly string[]>(
    () => session?.distinct_roles ?? [],
    [session],
  );

  // Resolution runs once per (session identity × URL-when-session-loaded).
  // We track the last session object we resolved for so a silent refresh
  // that returns the same distinct_roles doesn't reset the user's pick.
  const lastResolvedSessionRef = useRef<unknown>(null);

  useEffect(() => {
    if (sessionStatus !== "authenticated" || session === null) {
      // Not yet ready; keep showing the "resolving" state.
      setStatus("resolving");
      setCurrentRoleState(null);
      lastResolvedSessionRef.current = null;
      return;
    }

    if (lastResolvedSessionRef.current === session) {
      // Already resolved for this session; do not second-guess an explicit
      // user pick on re-renders.
      return;
    }
    lastResolvedSessionRef.current = session;

    const asParam = readAsParam(location.search);
    const storedRole = getCurrentRole();

    const resolved = resolveInitialRole({
      distinctRoles: session.distinct_roles,
      asParam,
      storedRole,
    });

    if (resolved.role !== null) {
      persistCurrentRole(resolved.role);
    } else if (storedRole !== null && !session.distinct_roles.includes(storedRole)) {
      // A stale role from a previous session is no longer valid — clear it
      // so the picker isn't fed a bad default.
      clearCurrentRole();
    }

    setCurrentRoleState(resolved.role);
    setStatus(resolved.status);
    // location.search is intentionally read at resolution time only; a
    // subsequent navigation must not re-apply `?as=` over a user's pick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionStatus, session]);

  const setRole = useCallback(
    (role: string): void => {
      if (!distinctRoles.includes(role)) {
        // Silent no-op — DESIGN.md §9 forbids setting a role the user does
        // not hold. UI surfaces (switcher, picker) filter against
        // distinctRoles so this path should only fire for buggy callers.
        // eslint-disable-next-line no-console
        console.warn(`[role] refusing to set role '${role}' — not in distinct_roles`);
        return;
      }
      persistCurrentRole(role);
      setCurrentRoleState(role);
      setStatus(distinctRoles.length === 1 ? "single" : "selected");
      // Bump the token so subscribers re-fetch scoped data without reload.
      setScopedFetchToken((n) => n + 1);
    },
    [distinctRoles],
  );

  const forcePickerOpen = useCallback((): boolean => {
    if (distinctRoles.length === 0) {
      // Nothing to pick; caller decides how to surface this.
      return false;
    }
    // Clear any stale selection so the picker isn't rendered with a
    // pre-highlighted bad default.
    clearCurrentRole();
    setCurrentRoleState(null);
    setStatus("pending");
    return true;
  }, [distinctRoles]);

  // Wire the api client's "missing role" handler to this provider so a
  // /api/* call made before a role has been picked triggers the picker
  // rather than the default redirect-to-/signin behavior (DESIGN.md §10,
  // "400 role_required → block the flow").
  useEffect(() => {
    setMissingRoleHandler(() => {
      // If the picker is already open (status==="pending"), do nothing.
      // Otherwise nudge status so the picker mounts.
      setStatus((s) => (s === "pending" ? s : distinctRoles.length <= 1 ? s : "pending"));
    });
  }, [distinctRoles]);

  const value = useMemo<RoleContextValue>(
    () => ({
      currentRole,
      status,
      distinctRoles,
      setRole,
      forcePickerOpen,
      scopedFetchToken,
    }),
    [currentRole, status, distinctRoles, setRole, forcePickerOpen, scopedFetchToken],
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}
